import React, { Suspense, useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { NormsProvider, useNorms } from './context/NormsContext';
import { validateTab } from './utils/validation';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AdminRoute from './components/auth/AdminRoute';
import PublicRoute from './components/auth/PublicRoute';
import { adminApi, normsApi } from './services/api';
import './App.css';
import TrueNormsLogo from './TrueNorms Logo PNG Version 1.svg';

const Login = React.lazy(() => import('./components/auth/Login'));
const ForgetPassword = React.lazy(() => import('./components/auth/ForgetPassword'));
const ResetPassword = React.lazy(() => import('./components/auth/ResetPassword'));
const SetPassword = React.lazy(() => import('./components/auth/SetPassword'));
const OtpVerification = React.lazy(() => import('./components/auth/OtpVerification'));
const AdminPanel = React.lazy(() => import('./components/admin/AdminPanel'));
const PatientDetails = React.lazy(() => import('./components/admin/PatientDetails'));
const PatientList = React.lazy(() => import('./components/patient/PatientList'));
const PatientHistory = React.lazy(() => import('./components/patient/PatientHistory'));
const GeneralTab = React.lazy(() => import('./components/tabs/GeneralTab'));
const ScreeningPremorbidTab = React.lazy(() => import('./components/tabs/ScreeningPremorbidTab'));
const ADASTab = React.lazy(() => import('./components/tabs/ADASTab'));
const IntellectualTab = React.lazy(() => import('./components/tabs/IntellectualTab'));
const AttentionTab = React.lazy(() => import('./components/tabs/AttentionTab'));
const WMSRTab = React.lazy(() => import('./components/tabs/WMSRTab'));
const ListLearningTab = React.lazy(() => import('./components/tabs/ListLearningTab'));
const LanguageTab = React.lazy(() => import('./components/tabs/LanguageTab'));
const VisuospatialTab = React.lazy(() => import('./components/tabs/VisuospatialTab'));
const MotorTab = React.lazy(() => import('./components/tabs/MotorTab'));
const ADLsTab = React.lazy(() => import('./components/tabs/ADLsTab'));
const PersonalityTab = React.lazy(() => import('./components/tabs/PersonalityTab'));
const ValidityTab = React.lazy(() => import('./components/tabs/ValidityTab'));

const AppContent: React.FC = () => {
  const {
    normsInfo,
    setNormsInfo,
    calculateNorms,
    generatePdf,
    savePatientDetails,
    savePatientDetailsOnContinue,
    loadSampleData,
    isLoading,
    error,
    clearError,
    errorFields,
    setErrorFields,
    setFieldErrorMessages,
    firstErrorTabId,
    setFirstErrorTabId,
  } = useNorms();
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('general');
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [selectedPatientId, setSelectedPatientId] = useState<number | ''>('');
  const [patients, setPatients] = useState<any[]>([]);
  const [showAddPatientModal, setShowAddPatientModal] = useState(false);
  const [patientSearchQuery, setPatientSearchQuery] = useState('');
  const [showPatientDropdown, setShowPatientDropdown] = useState(false);
  const patientDropdownRef = useRef<HTMLDivElement | null>(null);
  const [isCreatingPatient, setIsCreatingPatient] = useState(false);
  const defaultPatientId=126;
  const [patientForm, setPatientForm] = useState({
    firstName: '',
    lastName: '',
    fullName: '',
  });
  const [patientFormError, setPatientFormError] = useState('');
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'error' | 'success' } | null>(null);
  const toastTimerRef = useRef<number | null>(null);
  const [draftPrompt, setDraftPrompt] = useState<{
    patientDetailsId: number;
    patientId: number;
    patientName: string;
    normsData: any;
  } | null>(null);

  const showToast = (message: string, type: 'error' | 'success' = 'error') => {
    setToast({ message, type });
    if (toastTimerRef.current) {
      window.clearTimeout(toastTimerRef.current);
    }
    toastTimerRef.current = window.setTimeout(() => {
      setToast(null);
      toastTimerRef.current = null;
    }, 3500);
  };

  // Load norms data from Patient History "Edit" action
  useEffect(() => {
    const state = location.state as any;
    if (state && state.fromHistory && state.normsData && state.patientId) {
      const { normsData, patientId, fromUpload } = state;

      setSelectedPatientId(patientId);
      setIsNewPatient(false);
      setActiveTab('general');

      // Base data populated from history record
      let nextNormsInfo: any = {
        ...(normsData || {}),
      };

      // Only when coming from the "Upload" action do we override
      // the Date of Testing with today's date.
      if (fromUpload) {
        const today = new Date();
        const isoToday = today.toISOString().split('T')[0]; // yyyy-MM-dd
        nextNormsInfo.testingdate = isoToday;
      }

      setNormsInfo((prev: any) => ({
        ...(prev || {}),
        ...nextNormsInfo,
      }));

      // Clear navigation state so it doesn't reapply on refresh/navigate
      navigate('.', { replace: true, state: {} });
    }
  }, [location.state, navigate, setNormsInfo]);

  const loadPatients = async () => {
    try {
      // Get doctorId from localStorage
      const storedUser = localStorage.getItem('user');
      if (!storedUser) {
        console.error('No user found in localStorage');
        setPatients([]);
        return;
      }

      let doctorId: number | null = null;
      try {
        const parsed = JSON.parse(storedUser);
        if (parsed && typeof parsed.id === 'number') {
          doctorId = parsed.id;
        }
      } catch (err) {
        console.error('Failed to parse user from localStorage', err);
        setPatients([]);
        return;
      }

      if (doctorId === null) {
        console.error('No doctorId found in user data');
        setPatients([]);
        return;
      }

      const response = await adminApi.getPatientsByDoctorId(doctorId);
      setPatients(response.patients || []);
    } catch (err) {
      console.error('Failed to load patients for dropdown', err);
      setPatients([]);
    }
  };

  useEffect(() => {
    loadPatients();
  }, []);

  // When validation/PDF error is set (e.g. Generate PDF), show toast in top right
  useEffect(() => {
    if (error) showToast(error, 'error');
  }, [error]);

  // When PDF validation fails: switch to first error tab and focus the missing field
  useEffect(() => {
    if (!firstErrorTabId) return;
    setActiveTab(firstErrorTabId);
    const firstFieldName = errorFields[0];
    const timer = setTimeout(() => {
      if (firstFieldName) {
        const fieldElement = document.querySelector(`[data-field-name="${firstFieldName}"]`) as HTMLElement;
        if (fieldElement) {
          fieldElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          const focusable = fieldElement.tagName === 'INPUT' || fieldElement.tagName === 'SELECT' || fieldElement.tagName === 'TEXTAREA'
            ? fieldElement
            : (fieldElement.querySelector('input, select, textarea') as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null);
          if (focusable) focusable.focus();
        }
      }
      setFirstErrorTabId(null);
    }, 350);
    return () => clearTimeout(timer);
  }, [firstErrorTabId]); // eslint-disable-line react-hooks/exhaustive-deps -- errorFields set with firstErrorTabId

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) {
        window.clearTimeout(toastTimerRef.current);
      }
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (patientDropdownRef.current && !patientDropdownRef.current.contains(event.target as Node)) {
        setShowPatientDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Filter patients based on search query
  const filteredPatients = patients.filter((patient) => {
    if (!patientSearchQuery.trim()) return true;
    const query = patientSearchQuery.toLowerCase();
    return (
      (patient.firstName && patient.firstName.toLowerCase().includes(query)) ||
      (patient.lastName && patient.lastName.toLowerCase().includes(query)) ||
      (patient.fullName && patient.fullName.toLowerCase().includes(query))
    );
  });

  const selectedPatient = patients.find((p) => p.patientId === selectedPatientId);

  const tabs = [
    { id: 'general', label: 'General', icon: '📅', iconImage: '/general-icon.png' },
    { id: 'screening', label: 'Screening/Premorbid', icon: '📄', iconImage: '/screening-icon.png' },
    { id: 'adas', label: 'ADAS', icon: '👁️', iconImage: '/adas-icon.png' },
    { id: 'intellectual', label: 'Intellectual', icon: '🧠', iconImage: '/intellectual-icon.png' },
    { id: 'attention', label: 'Attention', icon: '⚠️', iconImage: '/attention-icon.png' },
    { id: 'wmsr', label: 'WMS-IV', icon: '📊', iconImage: '/wmsr-icon.png' },
    { id: 'listlearning', label: 'List Learning', icon: '📋', iconImage: '/listlearning-icon.png' },
    { id: 'language', label: 'Language', icon: '🌐', iconImage: '/language-icon.png' },
    { id: 'visuospatial', label: 'Visuospatial', icon: '🎯', iconImage: '/visuospatial-icon.png' },
    { id: 'motor', label: 'Motor', icon: '✋', iconImage: '/motor-icon.png' },
    { id: 'adls', label: 'ADLs', icon: '📝', iconImage: '/adls-icon.png' },
    { id: 'personality', label: 'Personality', icon: '👤', iconImage: '/personality-icon.png' },
    { id: 'validity', label: 'Validity', icon: '✅', iconImage: '/validity.png' },
    // Logout tab (no content, just triggers logout)  
    {
      id: 'logout',
      label: 'Logout',
      iconSvg: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
          <polyline points="16 17 21 12 16 7"></polyline>
          <line x1="21" y1="12" x2="9" y2="12"></line>
        </svg>
      ),
    },
  ];

  const [isNewPatient, setIsNewPatient] = useState(false);

  // After login, check once if there is any draft (isDraft = 1) for this doctor and prompt to continue
  useEffect(() => {
    let cancelled = false;

    const fetchDraft = async () => {
      try {
        if (!user) return;

        // Only show draft popup once per login session
        const storedEligible = localStorage.getItem('draftPromptEligible');
        const storedUserId = localStorage.getItem('draftPromptUserId');
        if (storedEligible !== '1' || !storedUserId || Number(storedUserId) !== user.id) {
          return;
        }

        const check = await normsApi.checkDraftForDoctor();
        if (!check.hasDraft || cancelled) {
          localStorage.setItem('draftPromptEligible', '0');
          return;
        }

        const latest = await normsApi.getLatestDraftForDoctor();
        if (
          !latest.hasDraft ||
          !latest.patientDetailsId ||
          !latest.patientId ||
          !latest.normsData ||
          cancelled
        ) {
          localStorage.setItem('draftPromptEligible', '0');
          return;
        }

        setDraftPrompt({
          patientDetailsId: latest.patientDetailsId,
          patientId: latest.patientId,
          patientName: latest.patientName || '',
          normsData: latest.normsData,
        });

        // Mark popup as shown for this login
        localStorage.setItem('draftPromptEligible', '0');
      } catch (err) {
        console.error('Failed to load draft patient details', err);
        try {
          localStorage.setItem('draftPromptEligible', '0');
        } catch {
          // ignore
        }
      }
    };

    fetchDraft();

    return () => {
      cancelled = true;
    };
  }, [user]);

  const handleNewCase = () => {
    setShowAddPatientModal(false);
    setSelectedPatientId('');
    setPatientSearchQuery('');
    setShowPatientDropdown(false);
    setErrorFields([]);
    const todayISO = new Date().toISOString().split('T')[0];
    setNormsInfo({ testingdate: todayISO });
    setIsNewPatient(true);
    setActiveTab('general');
    window.scrollTo({ top: 0, behavior: 'smooth' });

    // Reset history edit mode when starting a brand new patient
    try {
      localStorage.setItem('historyEditMode', '1');
    } catch {
      // Ignore storage errors
    }

    console.info('General form has been cleared.');
    console.info('Screening/Premorbid form has been cleared.');
    console.info('ADAS form has been cleared.');
    console.info('Intellectual form has been cleared.');
    console.info('Attention form has been cleared.');
    console.info('WMS-IV form has been cleared.');
    console.info('List Learning form has been cleared.');
    console.info('Language form has been cleared.');
    console.info('Visuospatial form has been cleared.');
    console.info('Motor form has been cleared.');
    console.info('ADLs form has been cleared.');
    console.info('Personality form has been cleared.');
    console.info('isNewPatient:', true);
  };

  const handleDraftContinue = () => {
    if (!draftPrompt) return;
    const { patientId, normsData } = draftPrompt;
    if (patientId && normsData) {
      setSelectedPatientId(patientId);
      setIsNewPatient(false);
      setActiveTab('general');
      setNormsInfo((prev: any) => ({
        ...(prev || {}),
        ...normsData,
      }));
    }
    setDraftPrompt(null);
  };

  const handleDraftDiscard = async () => {
    if (!draftPrompt) {
      setDraftPrompt(null);
      return;
    }
    try {
      await normsApi.discardDraftForDoctor(draftPrompt.patientDetailsId);
    } catch (err) {
      console.error('Failed to discard draft patient details', err);
      showToast('Failed to discard draft. Please try again.', 'error');
    } finally {
      setDraftPrompt(null);
    }
  };

  const handleSaveCase = () => {
    const data = normsInfo || {};
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: 'text/plain;charset=utf-8',
    });
    const a = document.createElement('a');
    const ptName = data.ptname || data.firstname || data.lastname || 'Patient';
    const date = new Date().toISOString().split('T')[0];
    a.href = window.URL.createObjectURL(blob);
    a.download = `NormsCase-${ptName}-${date}.txt`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(a.href);
    document.body.removeChild(a);
  };

  const handleOpenClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      try {
        const text = reader.result as string;
        const parsed = JSON.parse(text);
        setNormsInfo(parsed);
      } catch (err) {
        console.error('Failed to load case file:', err);
        window.alert('The selected file is not a valid Norms Retriever case (JSON txt).');
      }
    };
    reader.readAsText(file, 'utf-8');

    // Reset input so the same file can be chosen again later if needed
    event.target.value = '';
  };

  const handleContinue = async () => {
    // Require a selected patient before leaving General tab (unless creating a new patient)
    if (
      activeTab === 'general' &&
      !isNewPatient &&
      (!selectedPatientId || typeof selectedPatientId !== 'number')
    ) {
      showToast('Please select a patient before continuing.', 'error');
      return;
    }

    const validation = validateTab(activeTab, normsInfo);
    
    if (!validation.isValid) {
      setErrorFields(validation.errorFields);
      const messages: Record<string, string> = {};
      validation.errorFields.forEach((field, i) => {
        messages[field] = validation.errors[i] ?? '';
      });
      setFieldErrorMessages(messages);

      // Scroll to first invalid field
      setTimeout(() => {
        const firstErrorField = validation.errorFields[0];
        if (firstErrorField) {
          const fieldElement = document.querySelector(`[data-field-name="${firstErrorField}"]`) as HTMLElement;
          if (fieldElement) {
            fieldElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            fieldElement.focus();
          }
        }
      }, 100);
      return;
    }

    // Clear errors if validation passes
    setErrorFields([]);
    setFieldErrorMessages({});
    if (activeTab === 'general' && isNewPatient) {
      const firstName = normsInfo?.firstname || '';
      const lastName = normsInfo?.lastname || '';
      const fullName =
        normsInfo?.ptname ||
        `${firstName} ${lastName}`.trim();

      try {
        setIsCreatingPatient(true);
        const response = await adminApi.createPatient(
          firstName,
          lastName,
          fullName,
          {
            patSex: normsInfo?.sex || undefined,
            patRace: normsInfo?.race || undefined,
            patDOB: normsInfo?.dob || undefined,
            patOccupation: normsInfo?.occupation || undefined,
            patEducation: normsInfo?.education ?? undefined,
          }
        );
        if (response?.patient?.patientId) {
          setSelectedPatientId(response.patient.patientId);
        }
        await loadPatients();
        setIsNewPatient(false);
      } catch (err: any) {
        const status = err?.response?.status;
        const message =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          (status ? `Create patient failed (status ${status}).` : 'Create patient failed.');
        console.error('Failed to create patient from General tab:', err);
        showToast(message, 'error');
        return;
      } finally {
        setIsCreatingPatient(false);
      }
    }

    // Save patient details on Continue (separate endpoint for continue flow)
    if (typeof selectedPatientId === 'number') {
      try {
        const userId = user && typeof (user as any).id === 'number' ? (user as any).id : undefined;
        await savePatientDetailsOnContinue(selectedPatientId, userId);
      } catch (e) {
        console.error('Save patient details on Continue:', e);
        showToast('Data could not be saved. You can still continue.', 'error');
      }
    }

    const currentIndex = tabs.findIndex(tab => tab.id === activeTab);

    // If we're on the last content tab (Validity), generate the PDF instead of navigating
    if (activeTab === 'validity') {
      generatePdf(selectedPatientId);
      return;
    }

    if (currentIndex < tabs.length - 1) {
      setActiveTab(tabs[currentIndex + 1].id);
      // Scroll to top when navigating
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleBack = () => {
    const contentTabs = tabs.filter((tab) => tab.id !== 'logout');
    const currentIndex = contentTabs.findIndex((tab) => tab.id === activeTab);
    if (currentIndex > 0) {
      setActiveTab(contentTabs[currentIndex - 1].id);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleCreatePatient = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsCreatingPatient(true);
    setPatientFormError('');

    try {
      await adminApi.createPatient(
        patientForm.firstName,
        patientForm.lastName,
        patientForm.fullName
      );
      setShowAddPatientModal(false);
      setPatientForm({
        firstName: '',
        lastName: '',
        fullName: '',
      });
      await loadPatients();
      alert('Patient created successfully!');
    } catch (err: any) {
      setPatientFormError(err.response?.data?.error || 'Failed to create patient');
    } finally {
      setIsCreatingPatient(false);
    }
  };

  const handlePatientFormChange = (field: string, value: string) => {
    if (field === 'firstName') {
      const lastName = patientForm.lastName;
      const fullName = `${value} ${lastName}`.trim();
      setPatientForm({
        ...patientForm,
        firstName: value,
        fullName,
      });
    } else if (field === 'lastName') {
      const firstName = patientForm.firstName;
      const fullName = `${firstName} ${value}`.trim();
      setPatientForm({
        ...patientForm,
        lastName: value,
        fullName,
      });
    } else {
      setPatientForm({
        ...patientForm,
        [field]: value,
      });
    }
  };

  const renderTabContent = () => {
    // Validity is the last content tab (logout is after it)
    const isLastTab = activeTab === 'validity';
    const isFirstTab = activeTab === 'general';

    switch (activeTab) {
      case 'general':
        return (
          <GeneralTab
            onContinue={handleContinue}
            onBack={handleBack}
            isLastTab={isLastTab}
            isFirstTab={isFirstTab}
            errorFields={errorFields}
            isNewPatient={isNewPatient}
          />
        );
      case 'screening':
        return <ScreeningPremorbidTab onContinue={handleContinue} onBack={handleBack} isLastTab={isLastTab} isFirstTab={isFirstTab} errorFields={errorFields} />;
      case 'adas':
        return <ADASTab onContinue={handleContinue} onBack={handleBack} isLastTab={isLastTab} isFirstTab={isFirstTab} errorFields={errorFields} />;
      case 'intellectual':
        return <IntellectualTab onContinue={handleContinue} onBack={handleBack} isLastTab={isLastTab} isFirstTab={isFirstTab} errorFields={errorFields} />;
      case 'attention':
        return <AttentionTab onContinue={handleContinue} onBack={handleBack} isLastTab={isLastTab} isFirstTab={isFirstTab} errorFields={errorFields} />;
      case 'wmsr':
        return <WMSRTab onContinue={handleContinue} onBack={handleBack} isLastTab={isLastTab} isFirstTab={isFirstTab} errorFields={errorFields} />;
      case 'listlearning':
        return <ListLearningTab onContinue={handleContinue} onBack={handleBack} isLastTab={isLastTab} isFirstTab={isFirstTab} errorFields={errorFields} />;
      case 'language':
        return <LanguageTab onContinue={handleContinue} onBack={handleBack} isLastTab={isLastTab} isFirstTab={isFirstTab} errorFields={errorFields} />;
      case 'visuospatial':
        return <VisuospatialTab onContinue={handleContinue} onBack={handleBack} isLastTab={isLastTab} isFirstTab={isFirstTab} errorFields={errorFields} />;
      case 'motor':
        return <MotorTab onContinue={handleContinue} onBack={handleBack} isLastTab={isLastTab} isFirstTab={isFirstTab} errorFields={errorFields} />;
      case 'adls':
        return <ADLsTab onContinue={handleContinue} onBack={handleBack} isLastTab={isLastTab} isFirstTab={isFirstTab} errorFields={errorFields} />;
      case 'validity':
        return <ValidityTab onContinue={handleContinue} onBack={handleBack} isLastTab={isLastTab} isFirstTab={isFirstTab} errorFields={errorFields} />;
      case 'personality':
        return <PersonalityTab onContinue={handleContinue} onBack={handleBack} isLastTab={isLastTab} isFirstTab={isFirstTab} errorFields={errorFields} />;
      default:
        return (
          <GeneralTab
            onContinue={handleContinue}
            onBack={handleBack}
            isLastTab={isLastTab}
            isFirstTab={isFirstTab}
            errorFields={errorFields}
            isNewPatient={isNewPatient}
          />
        );
    }
  };

  const loadDefaultData=()=>{
      setSelectedPatientId(defaultPatientId);
    // setPatientSearchQuery('David Dvali');
     setShowPatientDropdown(false);
    loadSampleData(); 
  }
  return (
    <div className="App">
      {draftPrompt && (
        <div className="modal-overlay inactivity-modal-overlay draft-modal-overlay">
          <div
            className="modal-content inactivity-modal-content draft-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="inactivity-modal-header draft-modal-header">
              <span className="inactivity-modal-icon" aria-hidden>📝</span>
              <h2 className="inactivity-modal-title draft-modal-title">Continue with draft?</h2>
            </div>
            <p className="inactivity-modal-message draft-modal-message">
              You have a saved data for patient{' '}
              <strong>{draftPrompt.patientName || 'this patient'}</strong>. Do you want to continue ?
            </p>
            <div className="inactivity-modal-actions draft-modal-actions">
              <button
                type="button"
                className="inactivity-modal-btn inactivity-modal-continue draft-modal-continue-btn"
                onClick={handleDraftContinue}
              >
                Yes, continue
              </button>
              <button
                type="button"
                className="inactivity-modal-btn inactivity-modal-yes draft-modal-discard-btn"
                onClick={handleDraftDiscard}
              >
                Discard draft
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Top Header */}
      <header className="app-header">
        {/* Mobile: Hamburger menu icon on left */}
        <button 
          className="header-btn mobile-menu-btn" 
          onClick={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
          title="Menu"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            {isMobileSidebarOpen ? (
              <path d="M18 6L6 18M6 6l12 12" />
            ) : (
              <path d="M3 12h18M3 6h18M3 18h18" />
            )}
          </svg>
        </button>
        
        <div className="header-left">
          <img
            src="/logo.png"
            alt="Sugar Land Neuropsychology"
            className="header-logo"
            width={160}
            height={40}
          />
        </div>

                <div className="header-center">
          <img
            src={TrueNormsLogo}
            alt="TrueNorms"
            className="header-true-norms-logo"
          />
        </div>
        
        <div className="header-right">
          {/* Desktop: Show buttons in header */}
          <div className="header-buttons-desktop">
            {isAdmin && (
              <a href="/admin" className="header-btn admin-btn" title="Admin Panel">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
                <span>Admin</span>
              </a>
            )}
            {!isAdmin && (
              <button
                onClick={() => navigate('/patients')}
                className="header-btn admin-btn"
                title="Patient List"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
                <span>Patient List</span>
              </button>
            )}
            <button
              onClick={() => {
                generatePdf(selectedPatientId);
              }}
              className="header-btn generate-pdf-btn"
              disabled={isLoading || selectedPatientId === '' || typeof selectedPatientId !== 'number'}
              title={selectedPatientId === '' || typeof selectedPatientId !== 'number' ? 'Select a patient first' : 'Generate PDF'}
            >
              <span className="generate-pdf-text">Generate PDF</span>
              <span className="generate-pdf-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
              </span>
            </button>
          </div>
          <button onClick={logout} className="header-btn logout-btn logout-btn-desktop" title="Logout">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
              <polyline points="16 17 21 12 16 7"></polyline>
              <line x1="21" y1="12" x2="9" y2="12"></line>
            </svg>
          </button>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {isMobileSidebarOpen && (
        <div 
          className="mobile-sidebar-overlay" 
          onClick={() => setIsMobileSidebarOpen(false)}
        />
      )}

      <div className="app-layout">
        {/* Left Sidebar */}
        <aside className={`sidebar ${isMobileSidebarOpen ? 'mobile-open' : ''}`}>
          {/* Mobile: Close button */}
          <button 
            className="sidebar-close-btn"
            onClick={() => setIsMobileSidebarOpen(false)}
            title="Close Menu"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
          
          <nav className="sidebar-nav">
            {/* Mobile: Admin, Patient List, and Generate PDF buttons */}
            <div className="sidebar-mobile-actions">
              <div className="sidebar-actions-header">Actions</div>
              {isAdmin && (
                <a 
                  href="/admin" 
                  className="sidebar-item sidebar-action-btn" 
                  onClick={() => setIsMobileSidebarOpen(false)}
                >
                  <span className="sidebar-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                  </span>
                  <span className="sidebar-label">Admin</span>
                </a>
              )}
              {!isAdmin && (
                <button 
                  onClick={() => {
                    navigate('/patients');
                    setIsMobileSidebarOpen(false);
                  }} 
                  className="sidebar-item sidebar-action-btn"
                >
                  <span className="sidebar-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                      <circle cx="9" cy="7" r="4"></circle>
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                      <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                    </svg>
                  </span>
                  <span className="sidebar-label">Patient List</span>
                </button>
              )}
              <button 
                onClick={() => {
                  generatePdf(selectedPatientId);
                  setIsMobileSidebarOpen(false);
                }} 
                className="sidebar-item sidebar-action-btn generate-pdf-sidebar-btn" 
                disabled={isLoading || selectedPatientId === '' || typeof selectedPatientId !== 'number'}
                title={selectedPatientId === '' || typeof selectedPatientId !== 'number' ? 'Select a patient first' : 'Generate PDF'}
              >
                <span className="sidebar-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="7 10 12 15 17 10"></polyline>
                    <line x1="12" y1="15" x2="12" y2="3"></line>
                  </svg>
                </span>
                <span className="sidebar-label">Generate PDF</span>
              </button>
              <div className="sidebar-divider"></div>
            </div>
            
            <div className="sidebar-nav-header">Navigation</div>
            
            {/* Tab Navigation */}
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`sidebar-item ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => {
                  // Special handling for Logout tab
                  if (tab.id === 'logout') {
                    logout();
                    return;
                  }

                  // Validate current tab before switching
                  if (activeTab !== tab.id) {
                    const validation = validateTab(activeTab, normsInfo);
                    if (!validation.isValid) {
                      setErrorFields(validation.errorFields);
                      const messages: Record<string, string> = {};
                      validation.errorFields.forEach((field, i) => {
                        messages[field] = validation.errors[i] ?? '';
                      });
                      setFieldErrorMessages(messages);

                      // Scroll to first invalid field
                      setTimeout(() => {
                        const firstErrorField = validation.errorFields[0];
                        if (firstErrorField) {
                          const fieldElement = document.querySelector(`[data-field-name="${firstErrorField}"]`) as HTMLElement;
                          if (fieldElement) {
                            fieldElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            fieldElement.focus();
                          }
                        }
                      }, 100);
                      return;
                    }
                  }
                  // Clear errors when switching tabs
                  setErrorFields([]);
                  setFieldErrorMessages({});
                  setActiveTab(tab.id);
                  setIsMobileSidebarOpen(false);
                }}
              >
                <span className="sidebar-icon">
                  {tab.iconSvg ? (
                    tab.iconSvg
                  ) : tab.iconImage ? (
                    <img
                      src={tab.iconImage}
                      alt={tab.label}
                      width={20}
                      height={20}
                      loading="lazy"
                    />
                  ) : (
                    tab.icon
                  )}
                </span>
                <span className="sidebar-label">{tab.label}</span>
              </button>
            ))}
          </nav>
        </aside>

        {/* Main Content Area */}
        <main className="main-content">
          {/* Secondary Action Bar */}
          <div className="action-bar">
            <div className="action-bar-left">
              <label className="patient-select-label">
                <div className={`patient-select-wrapper ${showPatientDropdown ? 'show-dropdown' : ''}`} ref={patientDropdownRef}>
                  <div
                    className="patient-select"
                    onClick={() => {
                      if (!isLoading) {
                        if (!showPatientDropdown) {
                          setPatientSearchQuery('');
                        }
                        setShowPatientDropdown(!showPatientDropdown);
                      }
                    }}
                  >
                    <span className="patient-select-value">
                      {selectedPatient ? selectedPatient.fullName : '-- Select Patient --'}
                    </span>
                    <svg
                      className="patient-select-arrow"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </div>
                  {showPatientDropdown && (
                    <div className="patient-dropdown">
                      <div className="patient-search-container">
                        <label className="patient-search-label">Search by name</label>
                        <input
                          type="text"
                          className="patient-search-input"
                          placeholder="Type to search..."
                          value={patientSearchQuery}
                          onChange={(e) => {
                            setPatientSearchQuery(e.target.value);
                          }}
                          autoFocus
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      {filteredPatients.length > 0 ? (
                        filteredPatients.map((p) => (
                          <div
                            key={p.patientId}
                            className={`patient-dropdown-item ${selectedPatientId === p.patientId ? 'selected' : ''}`}
                            onMouseDown={(e) => {
                              e.preventDefault(); // Prevent input blur
                              setSelectedPatientId(p.patientId);
                              setIsNewPatient(false);
                              // Update norms info with selected patient's name details
                              setNormsInfo((prev: any) => ({
                                ...(prev || {}),
                                firstname: p.firstName || '',
                                lastname: p.lastName || '',
                                ptname: p.fullName || `${p.firstName || ''} ${p.lastName || ''}`.trim(),
                                education:p.patEducation ||'',
                                race:p.patRace||'',
                                sex: p.patSex ?? '',
                                occupation:p.patOccupation||'',
                                dob:p.patDOB||''
                              }));
                              setPatientSearchQuery('');
                              setShowPatientDropdown(false);
                            }}
                          >
                            <div className="patient-item-name">{p.fullName}</div>
                          </div>
                        ))
                      ) : (
                        <div className="patient-dropdown-item no-results">No patients found</div>
                      )}
                    </div>
                  )}
                </div>
              </label>
            </div>
            <div className="action-bar-right">
              <button onClick={handleNewCase} className="action-btn-header action-btn-mobile-icon-only" disabled={isLoading}>
                <span className="action-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ filter: 'brightness(0) invert(1)' }}>
                    <path d="M12 5v14M5 12h14"></path>
                  </svg>
                </span>
                <span>New Patient</span>
              </button>
              <div className="action-btn-separator"></div>
              <button onClick={loadDefaultData} className="action-btn-header action-btn-mobile-icon-only" disabled={isLoading}>
                <span className="action-icon">
                  <img
                    src="/load-icon.png"
                    alt="Load Sample Data"
                    width={16}
                    height={16}
                    loading="lazy"
                    style={{
                      width: '16px',
                      height: '16px',
                      objectFit: 'contain',
                      filter: 'brightness(0) invert(1)',
                    }}
                  />
                </span>
                <span>Load Sample Data</span>
              </button>
              <div className="calculate-norms-btn-wrapper">
                <div className="action-btn-separator"></div>
                <button onClick={calculateNorms} className="action-btn-header" disabled={isLoading} aria-hidden>
                  <span className="action-icon">
                    <img
                      src="/calculate-norms-icon.png"
                      alt="Calculate Norms"
                      width={16}
                      height={16}
                      loading="lazy"
                      style={{
                        width: '16px',
                        height: '16px',
                        objectFit: 'contain',
                        filter: 'brightness(0) invert(1)',
                      }}
                    />
                  </span>
                  <span>Calculate Norms</span>
                </button>
              </div>
            </div>
          </div>

          {/* Form Content */}
          <div className="content-wrapper">
            {error && (
              <div className="error-message">
                <span>{error}</span>
                <button onClick={clearError}>×</button>
              </div>
            )}

            <Suspense fallback={<div className="tab-loading">Loading section…</div>}>
              <div className="tab-content">{renderTabContent()}</div>
            </Suspense>
          </div>
        </main>
      </div>

      {isLoading && (
        <div className="loading-overlay">
          <div className="spinner">Loading...</div>
        </div>
      )}

      {toast && (
        <div className="toast-container">
          <div className={`toast toast-${toast.type}`}>{toast.message}</div>
        </div>
      )}

      {/* Hidden file input for "Open" case */}
      <input
        type="file"
        accept=".txt,application/json,text/plain"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      {/* Add Patient Modal */}
      {showAddPatientModal && (
        <div className="modal-overlay" onClick={() => setShowAddPatientModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add New Patient</h2>
              <button
                className="modal-close-btn"
                onClick={() => {
                  setShowAddPatientModal(false);
                  setPatientForm({
                    firstName: '',
                    lastName: '',
                    fullName: '',
                  });
                  setPatientFormError('');
                }}
              >
                ×
              </button>
            </div>
            {patientFormError && (
              <div className="modal-error">{patientFormError}</div>
            )}
            <form onSubmit={handleCreatePatient} className="modal-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="modalPatientFirstName">First Name *</label>
                  <input
                    type="text"
                    id="modalPatientFirstName"
                    value={patientForm.firstName}
                    onChange={(e) => handlePatientFormChange('firstName', e.target.value)}
                    placeholder="John"
                    required
                    disabled={isCreatingPatient}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="modalPatientLastName">Last Name *</label>
                  <input
                    type="text"
                    id="modalPatientLastName"
                    value={patientForm.lastName}
                    onChange={(e) => handlePatientFormChange('lastName', e.target.value)}
                    placeholder="Doe"
                    required
                    disabled={isCreatingPatient}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="modalPatientFullName">Full Name *</label>
                  <input
                    type="text"
                    id="modalPatientFullName"
                    value={patientForm.fullName}
                    onChange={(e) => handlePatientFormChange('fullName', e.target.value)}
                    placeholder="John Doe"
                    required
                    disabled={isCreatingPatient}
                  />
                </div>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="modal-cancel-btn"
                  onClick={() => {
                    setShowAddPatientModal(false);
                    setPatientForm({
                      firstName: '',
                      lastName: '',
                      fullName: '',
                    });
                    setPatientFormError('');
                  }}
                  disabled={isCreatingPatient}
                >
                  Cancel
                </button>
                <button type="submit" className="modal-submit-btn" disabled={isCreatingPatient}>
                  {isCreatingPatient ? 'Creating...' : 'Create Patient'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const INACTIVITY_MS = 60 * 60 * 1000; // 60 minutes

const MODAL_COUNTDOWN_SEC = 30;

const InactivityWatcher: React.FC = () => {
  const { isAuthenticated, logout } = useAuth();
  const [showInactivityModal, setShowInactivityModal] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const resetInactivityTimer = React.useCallback(() => {
    if (!isAuthenticated) return;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      setShowInactivityModal(true);
    }, INACTIVITY_MS);
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setShowInactivityModal(false);
      return;
    }
    resetInactivityTimer();
    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    const onActivity = () => resetInactivityTimer();
    events.forEach((ev) => window.addEventListener(ev, onActivity));
    return () => {
      events.forEach((ev) => window.removeEventListener(ev, onActivity));
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [isAuthenticated, resetInactivityTimer]);

  // 30-second countdown when modal is open; auto-logout at 0
  useEffect(() => {
    if (!showInactivityModal) return;
    setCountdown(MODAL_COUNTDOWN_SEC);
    countdownIntervalRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
          }
          setShowInactivityModal(false);
          logout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    };
  }, [showInactivityModal, logout]);

  const handleLogout = () => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    setShowInactivityModal(false);
    logout();
  };

  const handleContinue = () => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    setShowInactivityModal(false);
    resetInactivityTimer();
  };

  if (!showInactivityModal) return null;
  const progressPercent = (countdown / MODAL_COUNTDOWN_SEC) * 100;
  return (
    <div className="modal-overlay inactivity-modal-overlay">
      <div className="modal-content inactivity-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="inactivity-modal-header">
          <span className="inactivity-modal-icon" aria-hidden>⏱</span>
          <h2 className="inactivity-modal-title">Session timeout</h2>
        </div>
        <p className="inactivity-modal-message">Are you sure you want to logout?</p>
        <div className="inactivity-modal-loader">
          <div className="inactivity-modal-countdown-box">
            <span className="inactivity-modal-countdown-label">You will be logged out in</span>
            <span className="inactivity-modal-countdown-value">{countdown}</span>
            <span className="inactivity-modal-countdown-unit">seconds</span>
          </div>
          <div className="inactivity-modal-progress-track">
            <div className="inactivity-modal-progress-fill" style={{ width: `${progressPercent}%` }} />
          </div>
        </div>
        <div className="inactivity-modal-actions">
          <button type="button" className="inactivity-modal-btn inactivity-modal-continue" onClick={handleContinue}>
            Continue
          </button>
          <button type="button" className="inactivity-modal-btn inactivity-modal-yes" onClick={handleLogout}>
            Yes, logout
          </button>
        </div>
      </div>
    </div>
  );
};

const App: React.FC = () => {
  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      const el = e.target as Node | null;
      if (el instanceof HTMLInputElement && el.type === 'number') {
        e.preventDefault();
      }
    };
    window.addEventListener('wheel', handleWheel, { passive: false });
    return () => window.removeEventListener('wheel', handleWheel);
  }, []);

  return (
    <Router>
      <AuthProvider>
        <InactivityWatcher />
        <Suspense fallback={<div className="route-loading">Loading…</div>}>
          <Routes>
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              }
            />
            <Route
              path="/otp"
              element={
                <PublicRoute>
                  <OtpVerification />
                </PublicRoute>
              }
            />
            <Route
              path="/forgot-password"
              element={
                <PublicRoute>
                  <ForgetPassword />
                </PublicRoute>
              }
            />
            <Route
              path="/reset-password"
              element={
                <PublicRoute>
                  <ResetPassword />
                </PublicRoute>
              }
            />
            <Route
              path="/set-password"
              element={
                <ProtectedRoute>
                  <SetPassword />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <AdminRoute>
                  <AdminPanel />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/patients/:patientId/history"
              element={
                <AdminRoute>
                  <PatientHistory />
                </AdminRoute>
              }
            />
            <Route
              path="/patients/:patientId/history"
              element={
                <ProtectedRoute>
                  <PatientHistory />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/patients/:patientId"
              element={
                <AdminRoute>
                  <PatientDetails />
                </AdminRoute>
              }
            />
            <Route
              path="/patients/:patientId"
              element={
                <ProtectedRoute>
                  <PatientDetails />
                </ProtectedRoute>
              }
            />
            <Route
              path="/patients"
              element={
                <ProtectedRoute>
                  <PatientList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <NormsProvider>
                    <AppContent />
                  </NormsProvider>
                </ProtectedRoute>
              }
            />
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <NormsProvider>
                    <AppContent />
                  </NormsProvider>
                </ProtectedRoute>
              }
            />
          </Routes>
        </Suspense>
      </AuthProvider>
    </Router>
  );
};

export default App;
